import { useMemo } from 'react';
import {
    formatTime as formatTimeUtil,
    formatDateOnly as formatDateOnlyUtil,
    formatDateTime as formatDateTimeUtil,
    formatTimeAndDate as formatTimeAndDateUtil,
    formatSmartShortTimeAndDate as formatSmartShortTimeAndDateUtil,
    formatDateRange as formatDateRangeUtil,
    FormatOptions
} from '../util/format-date';
import { useCommonTranslation } from './use-common-translation';
import { useTimeSettings } from './use-time-settings';

/**
 * The date formatters, bound to the viewer's time settings and language.
 *
 * This is the seam where React state meets the pure helpers in
 * `util/format-date`: the timezone and clock come from the company user, the
 * locale and the words come from i18next, and the helpers stay free of both.
 */
export function useFormatDate() {
    const { t, i18n } = useCommonTranslation();
    const timeSettings = useTimeSettings();

    const opts: FormatOptions = useMemo(
        () => ({
            ...timeSettings,
            // `language` is empty until i18next has initialised; en-GB is the
            // configured fallback, so a date never renders in an unknown locale.
            locale: i18n.language || 'en-GB'
        }),
        [timeSettings, i18n.language]
    );

    return useMemo(() => {
        return {
            formatTime: (date: Date) => formatTimeUtil(date, opts),
            formatDateOnly: (date: Date) => formatDateOnlyUtil(date, opts),
            formatDateTime: (date: Date) => formatDateTimeUtil(date, opts),
            formatTimeAndDate: (date: Date) =>
                formatTimeAndDateUtil(date, opts),
            /**
             * "Today 14:32", "Yesterday 09:05", or "12 Mar 09:05" for anything
             * older. The helper decides which of the three it is; the wording
             * and the order of the parts are a translation, so they happen
             * here.
             */
            formatSmartShortTimeAndDate: (date: Date) => {
                const result = formatSmartShortTimeAndDateUtil(date, opts);

                if (result.kind === 'today') {
                    return t('shared.dates.today_at', { time: result.time });
                }
                if (result.kind === 'yesterday') {
                    return t('shared.dates.yesterday_at', {
                        time: result.time
                    });
                }
                return t('shared.dates.date_at', {
                    date: result.date,
                    time: result.time
                });
            },
            formatDateRange: (from: Date, to: Date) =>
                formatDateRangeUtil(from, to, opts)
        };
    }, [opts, t]);
}
